import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ITrainersData,
  ITrainersPage,
  ITrainersTool,
  ITrainerSubmit,
} from '@/types/trainers';
import {
  getTrainersContent,
  submitBenchmark,
  submitTrainersResponse,
  voidConsensus,
} from '@/api/qa';
import { HEADER_HEIGHT } from '@/utils/explorer';
import { useTrainerFeedback } from '@/hooks/useTrainerFeedback';
import Feedback from '@/components/ui/trainers/shared/Feedback';
import { useTrainerReviews } from '@/hooks/useTrainerReviews';
import { useUser } from '@/hooks/useUser';
import TrainerHeader from './TrainerHeader';
import TrainerFooter from './TrainerFooter';
import TrainerController, {
  TrainerButtons,
  TrainerInputs,
  TrainerSelect,
} from './TrainerController';
import TrainerButton from './TrainerButton';
import ErrorMessage from './shared/ErrorMessage';
import PositionAzimuthTrainerContent from './content/PositionAzimuthTrainerContent';

interface Props extends ITrainersPage {
  data?: ITrainersData;
  tool?: ITrainersTool;
  modifyPosition?: boolean;
  modifyAzimuth?: boolean;
}

const PositionAzimuth: React.FC<Props> = ({
  data: initialData,
  page,
  name,
  description: initialDescription,
  location,
  tool,
  modifyPosition = true,
  modifyAzimuth = true,
}) => {
  const { user } = useUser();
  const headerRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const toolId = initialData?.toolId || '';
  const [data, setData] = useState<ITrainersData | undefined>(initialData);
  const { incrementReviews } = useTrainerReviews();
  const {
    leaseId,
    lat: initialLat,
    lon: initialLon,
    azimuth: initialAzimuth,
  } = data?.content || {};

  const description = data?.content
    ? initialDescription ||
      `Find the outlined element in the image (at left) on the aerial map (at right) by adjusting the circle's location`
    : '';

  const isDataReady = Boolean(data?.content);
  const [isBenchmark, setIsBenchmark] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);
  const { feedback, setFeedback } = useTrainerFeedback();
  const [lat, setLat] = useState<number>(initialLat);
  const [lon, setLon] = useState<number>(initialLon);
  const [azimuth, setAzimuth] = useState<number>(initialAzimuth);

  const clearAllData = () => {
    setData(undefined);
    setIsBenchmark(false);
  };

  const refetchClient = useCallback(
    async (id: string) => {
      const _data: ITrainersData = await getTrainersContent(id, page);
      setData(_data);
    },
    [page],
  );

  const onBenchmarkToggle = (checked: boolean) => {
    setIsBenchmark(checked);
  };

  const positionPayload = (lat: number, lon: number) => {
    return lat !== undefined && lon !== undefined
      ? { points: [{ lat, lon }] }
      : {};
  };

  const azimuthPayload = (azimuth: number) => {
    return {
      annotations: [
        {
          id: 0,
          text: azimuth.toString(),
        },
      ],
    };
  };

  const posAziPayload = (lat: number, lon: number, azimuth: number) => {
    return {
      pos: { lon, lat },
      azi: azimuth,
    };
  };

  const buildPayload = (lat: number, lon: number, azimuth: number) => {
    if (modifyPosition && modifyAzimuth) {
      return posAziPayload(lat, lon, azimuth);
    }

    if (modifyPosition && !modifyAzimuth) {
      return positionPayload(lat, lon);
    }

    if (!modifyPosition && modifyAzimuth) {
      return azimuthPayload(azimuth);
    }

    return {};
  };

  const onSubmit = async ({ outcome }: ITrainerSubmit) => {
    clearAllData();
    if (!leaseId) {
      return;
    }

    const sending = {
      leaseId,
      ...(outcome ? { outcome } : buildPayload(lat, lon, azimuth)),
    };

    console.log('sending', sending);
    if (isBenchmark) {
      await submitBenchmark(toolId, sending);
    }

    const { error, benchmarkFeedback } = await submitTrainersResponse(
      toolId,
      sending,
    );

    if (benchmarkFeedback) {
      setFeedback({
        ...benchmarkFeedback,
        instructionsLink: tool?.args?.instructions,
      });
    }
    if (error) {
      setData({ error });
    } else {
      incrementReviews(toolId);
      refetchClient(toolId);
    }
  };

  useEffect(() => {
    setAzimuth(initialAzimuth);
    setLat(initialLat);
    setLon(initialLon);
  }, [initialAzimuth, initialLat, initialLon]);

  useEffect(() => {
    const calculateContentHeight = () => {
      const pageHeaderHeight = HEADER_HEIGHT;
      const sectionHeaderHeight = headerRef.current?.offsetHeight ?? 0;
      const controllerHeight = controllerRef.current?.offsetHeight ?? 0;
      const footerHeight = footerRef.current?.offsetHeight ?? 0;
      const newContentHeight =
        window.innerHeight -
        pageHeaderHeight -
        sectionHeaderHeight -
        controllerHeight -
        footerHeight;
      setContentHeight(newContentHeight);
    };

    calculateContentHeight();
    window.addEventListener('resize', calculateContentHeight);

    return () => window.removeEventListener('resize', calculateContentHeight);
  }, [leaseId]);

  const getMainButtonLabel = () => {
    const isPositionUnchanged =
      modifyPosition && initialLat === lat && initialLon === lon;
    const isAzimuthUnchanged = modifyAzimuth && initialAzimuth === azimuth;

    if (modifyPosition && modifyAzimuth) {
      if (isPositionUnchanged && isAzimuthUnchanged) {
        return 'Confirm Position and Azimuth';
      }
      return 'Update Position and Azimuth';
    }

    const positionLabel = modifyPosition
      ? isPositionUnchanged
        ? 'Confirm Position'
        : 'Update Position'
      : '';

    const azimuthLabel = modifyAzimuth
      ? isAzimuthUnchanged
        ? 'Confirm Azimuth'
        : 'Update Azimuth'
      : '';

    return `${positionLabel}${modifyPosition && modifyAzimuth ? ' and ' : ''}${azimuthLabel}`;
  };

  const shouldMainButtonBeDisabled = () => {
    if (tool?.widget === 'picker') {
      const isPositionUnchanged =
        modifyPosition && initialLat === lat && initialLon === lon;
      if (
        !data?.content?.positionHypotheses ||
        data?.content?.positionHypotheses.length === 0 ||
        isPositionUnchanged
      ) {
        return true;
      }
    }

    return false;
  };

  return (
    <div className="flex size-full flex-col">
      <TrainerHeader
        data={data}
        name={name}
        description={description}
        location={location}
        tool={tool}
        elementRef={headerRef}
      />
      {data?.error ? (
        <ErrorMessage error={data.error} />
      ) : (
        <>
          <PositionAzimuthTrainerContent
            data={data}
            tool={tool}
            contentHeight={contentHeight}
            modifyPosition={modifyPosition}
            modifyAzimuth={modifyAzimuth}
            showAzimuth={modifyAzimuth}
            setData={setData}
            lat={lat}
            setLat={setLat}
            lon={lon}
            setLon={setLon}
            azimuth={azimuth}
            setAzimuth={setAzimuth}
          />
          <TrainerController
            containerRef={controllerRef}
            isDataReady={isDataReady}
            onSubmit={onSubmit}
            data={data}
            tool={tool}>
            <TrainerButton
              onClick={() => onSubmit({})}
              disabled={!isDataReady || shouldMainButtonBeDisabled()}
              label={getMainButtonLabel()}
            />
            <TrainerButtons />
            <TrainerSelect />
            <TrainerInputs />
            {modifyAzimuth &&
            !modifyPosition &&
            (user?.advancedReviewer || user?.admin) ? (
              <TrainerButton
                onClick={async () => {
                  if (!leaseId || !toolId) {
                    setData({ error: 'Error kicking back element.' });
                    return;
                  }
                  const result = await voidConsensus(leaseId, toolId);
                  if (result?.error) {
                    setData({
                      error: `Error kicking back element. ${result.error}`,
                    });
                    return;
                  }
                  clearAllData();
                  refetchClient(toolId);
                }}
                disabled={!isDataReady}
                label={
                  !modifyPosition && modifyAzimuth
                    ? 'https://hivemapper-static.s3.us-west-2.amazonaws.com/website/public/assets/trainers/SendBackToPositioningIcon.svg'
                    : 'https://hivemapper-static.s3.us-west-2.amazonaws.com/website/public/assets/trainers/SendBackIcon.svg'
                }
              />
            ) : null}
          </TrainerController>
        </>
      )}
      <TrainerFooter
        data={data}
        elementRef={footerRef}
        onBenchmarkToggle={onBenchmarkToggle}
        isBenchmark={isBenchmark}
      />
      {feedback?.feedbackMessage && <Feedback />}
    </div>
  );
};

export default PositionAzimuth;
